import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { DeleteBucketIcon } from '@/assets/static/icons/delete-bucket';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import axios from 'axios';
import { Dispatch, SetStateAction, useState } from 'react';
import styled from 'styled-components';

interface DeleteCustomExportTemplateProps {
  isOpen: boolean;
  funnelId: string;
  setCustomExportTemplateName: Dispatch<SetStateAction<string>>;
  onClose: () => void;
}

const DeleteCustomExportTemplate = ({ isOpen, funnelId, setCustomExportTemplateName, onClose }: DeleteCustomExportTemplateProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSave = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);

      await axios.delete(`${process.env.REACT_APP_BACKEND_BASE_URL}/due-diligence-funnel/${funnelId}/delete-docx-template`, {
        headers: {
          authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      setCustomExportTemplateName('');
      onClose();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      showCloseIcon={!loading}
      showCancelButton={!loading}
      isUseOutsideClick={!loading}
      cancelButtonName="No, Cancel"
      confirmButton={{ name: 'Yes, Delete', onClick: onSave, disabled: loading }}
    >
      {errorMessage && (
        <ErrorBannerWrapper>
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        </ErrorBannerWrapper>
      )}
      <ModalContainer>
        <DeleteBucketIcon width={46} height={60} />
        {loading ? (
          <ModalLoader />
        ) : (
          <>
            <ModalTitle>Please confirm you want to delete this custom export template.</ModalTitle>
            <ModalDescription>There is no undo for this operation.</ModalDescription>
          </>
        )}
      </ModalContainer>
    </ModalWrappedContent>
  );
};

export default DeleteCustomExportTemplate;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;

const ModalContainer = styled.div`
  text-align: center;
  font-weight: 400;
  padding: 10px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const ModalTitle = styled.div`
  font-size: 33px;
  line-height: 46.2px;
  color: ${themeVar('font.strong')};
`;

const ModalDescription = styled.div`
  font-size: 16px;
  line-height: 22.4px;
  color: ${themeVar('font.base')};
`;
